import React, { useEffect, useMemo, useRef } from 'react';
import { Send, ChevronLeft, MessagesSquare } from 'lucide-react';
import { theme } from './theme';
import { PmMessage, messageTime } from './messaging';
import { useViewportFill } from './useViewportFill';
import './portals.css';

// ============================================================
// MessageThread — one conversation, drawn the same for both sides.
//
// PRESENTATIONAL ON PURPOSE. The owner screen and the admin inbox each own
// their own loading, polling and sending; they hand this component a list of
// messages and a way to send one. That is what keeps the two sides looking
// like the same product instead of two chats that drifted apart, and it is
// why `meSide` exists — the only thing that differs between them is which
// bubbles are yours.
// ============================================================

interface Props {
  title: string;
  subtitle?: string;
  /** Whose bubbles sit on the right. */
  meSide: 'admin' | 'participant';
  messages: PmMessage[];
  loading?: boolean;
  sending?: boolean;
  error?: string | null;
  /** Shown in place of the list when there is nothing yet. */
  emptyTitle: string;
  emptyText: string;
  placeholder: string;
  sendLabel: string;
  onSend: (body: string) => void;
  onBack?: () => void;
  /** Buttons for the header bar (archive, view-as, …). */
  headerActions?: React.ReactNode;
  /** Locale for the timestamps — the app already knows which one is on. */
  locale?: string;
  disabled?: boolean;
  /** Rendered under the header, above the messages (notices, context). */
  banner?: React.ReactNode;
  /** Extra class on the root — carries the --pm-chat-bottom offset variant. */
  className?: string;
}

const dayKey = (iso: string) => (iso || '').slice(0, 10);

const MessageThread: React.FC<Props> = ({
  title, subtitle, meSide, messages, loading, sending, error,
  emptyTitle, emptyText, placeholder, sendLabel,
  onSend, onBack, headerActions, locale = 'en-GB', disabled, banner, className,
}) => {
  const [draft, setDraft] = React.useState('');
  // A chat ends at the bottom of the screen, with the composer parked there
  // and the messages scrolling behind it. What it must NOT do is run to its
  // natural height and strand the composer mid-page above a field of empty
  // background, which is what a plain block layout gives here — the shell
  // around this screen is taller than the conversation inside it.
  const rootRef = useViewportFill<HTMLDivElement>();
  const listRef = useRef<HTMLDivElement | null>(null);

  const mine = (m: PmMessage) => (meSide === 'admin' ? m.from === 'admin' : m.from !== 'admin');

  // Jump to the newest message whenever the list grows. Keyed on the count
  // rather than the array so a poll that returns the same messages does not
  // yank the view away from someone reading back through the thread. Scrolls
  // the list, never the page: scrollIntoView would drag the whole shell.
  useEffect(() => {
    const el = listRef.current;
    if (!el || !messages.length) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  // Day separators: a chat that repeats the date on every bubble reads like a
  // log file, and one that never shows it leaves you guessing.
  const groups = useMemo(() => {
    const out: { day: string; items: PmMessage[] }[] = [];
    messages.forEach((m) => {
      const k = dayKey(m.createdAt);
      const last = out[out.length - 1];
      if (last && last.day === k) last.items.push(m);
      else out.push({ day: k, items: [m] });
    });
    return out;
  }, [messages]);

  const submit = () => {
    const body = draft.trim();
    if (!body || sending || disabled) return;
    setDraft('');
    onSend(body);
  };

  const dayLabel = (day: string) => {
    if (!day) return '';
    const d = new Date(`${day}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) return locale.startsWith('de') ? 'Heute' : 'Today';
    const yest = new Date(today.getTime() - 86400000);
    if (d.toDateString() === yest.toDateString()) return locale.startsWith('de') ? 'Gestern' : 'Yesterday';
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  };

  return (
    <div ref={rootRef} className={className ? `pm-chat ${className}` : 'pm-chat'}>
      <div className="pm-chat-head">
        {onBack && (
          <button onClick={onBack} className="pt-tap pm-chat-back" aria-label="Back">
            <ChevronLeft size={20} />
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="pm-chat-title">{title}</div>
          {subtitle && <div className="pm-chat-sub">{subtitle}</div>}
        </div>
        {headerActions}
      </div>

      {banner}

      <div className="pm-chat-list" ref={listRef}>
        {loading && !messages.length && (
          <>
            <div className="pt-skel" style={{ height: 54, width: '70%', marginBottom: 10 }} />
            <div className="pt-skel" style={{ height: 40, width: '55%', marginLeft: 'auto', marginBottom: 10 }} />
            <div className="pt-skel" style={{ height: 62, width: '75%' }} />
          </>
        )}

        {!loading && !messages.length && (
          <div className="pm-chat-empty">
            <MessagesSquare size={30} color={theme.gold} />
            <div className="pm-chat-empty-title">{emptyTitle}</div>
            <div className="pm-chat-empty-text">{emptyText}</div>
          </div>
        )}

        {groups.map((g) => (
          <div key={g.day}>
            <div className="pm-chat-day"><span>{dayLabel(g.day)}</span></div>
            {g.items.map((m) => {
              const isMine = mine(m);
              return (
                <div key={m.id} className={`pm-chat-row ${isMine ? 'is-mine' : ''}`}>
                  <div className={`pm-chat-bubble ${isMine ? 'is-mine' : ''}`}>
                    {!isMine && m.authorName && <div className="pm-chat-author">{m.authorName}</div>}
                    <div className="pm-chat-body">{m.body}</div>
                    <div className="pm-chat-time">{messageTime(m.createdAt, locale)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {error && <div className="pm-chat-error">{error}</div>}

      <div className="pm-chat-composer">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
          }}
          placeholder={placeholder}
          rows={1}
          enterKeyHint="send"
          disabled={disabled}
          className="pm-chat-input"
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || !!sending || !!disabled}
          aria-label={sendLabel}
          title={sendLabel}
          className="pt-tap pm-chat-send"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

export default MessageThread;
